import { cloneElement, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiXMark } from "react-icons/hi2";
import styled from "styled-components";

const Dialog = styled.dialog`
    border: none;
    border-radius: 1rem;
    padding: 0;
    margin: auto;
    width: calc(100% - 2rem);
    max-width: 680px;
    max-height: 90vh;
    background: white;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    &::backdrop {
        background: rgba(0, 0, 0, 0.3);
        backdrop-filter: blur(8px);
    }
`;
const CloseButton = styled.button`
    display: block;
    margin: 0.5rem 0.5rem 0.5rem auto;
    padding: 0.5rem;
    background: none;
    border: none;
    cursor: pointer;
    & svg { width: 2rem; height: 2rem; }
`;
const ModalContext = createContext();

function Modal({ children }) {
    const [openName, setOpenName] = useState("");
    const triggerRef = useRef(null);
    const close = useCallback(() => setOpenName(""), []);
    return <ModalContext.Provider value={{ openName, close, open: setOpenName, triggerRef }}>{children}</ModalContext.Provider>;
}

function Open({ children, opens }) {
    const { open, triggerRef } = useContext(ModalContext);
    return cloneElement(children, {
        onClick: (event) => {
            children.props.onClick?.(event);
            if (!event.defaultPrevented) {
                triggerRef.current = event.currentTarget;
                open(opens);
            }
        },
        "aria-haspopup": "dialog",
    });
}

function Window({ children, name, label = "Event details" }) {
    const { openName, close, triggerRef } = useContext(ModalContext);
    const ref = useRef(null);
    const isOpen = openName === name;
    useEffect(() => {
        if (!isOpen) return;
        const dialog = ref.current;
        const trigger = triggerRef.current;
        // Native modal dialogs provide focus containment, inert background and Escape.
        dialog.showModal();
        return () => {
            dialog.close();
            if (trigger?.isConnected) trigger.focus();
        };
    }, [isOpen, triggerRef]);
    if (!isOpen) return null;
    return createPortal(
        <Dialog ref={ref} aria-label={label} aria-modal="true"
            onCancel={(event) => { event.preventDefault(); close(); }}
            onClick={(event) => {
                if (event.target !== event.currentTarget) return;
                const rect = event.currentTarget.getBoundingClientRect();
                if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
            }}>
            <CloseButton type="button" aria-label="Close dialog" onClick={close}><HiXMark /></CloseButton>
            {cloneElement(children, { onCloseModal: close })}
        </Dialog>, document.body,
    );
}
Modal.Open = Open;
Modal.Window = Window;
export default Modal;
